import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import QuestionAnswer from "../components/molecules/QuestionAnswer";
import { Dimension, DimensionType, QuestionOptionType } from "../types/types";
import Heading from "../components/atoms/DimensionHeading";
import AtomicAppBar from "../components/molecules/AppBar";
import { useNavigate } from "react-router-dom";
import { RefetchCache, useCacheData } from "../utils/cache";
import AtomicTypography from "../components/atoms/Typography";
import generateExcel from "../components/organisms/excelGenerator";
import CircularProgressBar from "../components/atoms/CircularProgressBar";
import { QueryClient } from "react-query";


const DimensionStepper: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [dimensions, setDimensions] = useState<DimensionType[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<Dimension[]>([]);
  const [questionOption, setQuestionOption] = useState<QuestionOptionType[]>(
    []
  );
  const numberOfSteps = Number(process.env?.REACT_APP_NUMBER_OF_DIMENSIONS) || 6; 
  const [loading, setLoading] = useState(true);  
const queryClient = new QueryClient();
  const navigate = useNavigate();
  // Fetch dimensions data from cache
  const { data: dimensionsData,error: dimensionsError } = useCacheData<DimensionType[]>(
    "DimensionsData", // Key for caching
    "dimension", // URL to fetch data from
    Infinity // Time in seconds to cache data
  );
  // Fetch question options data from cache
  const { data: questionOptionsData,error: questionOptionsError } = useCacheData<QuestionOptionType[]>(
    "questionOptionsData",
    "questionOption",
    Infinity
  );

  useEffect(() => {
    // If dimensions data is present, set the state
    if (dimensionsData) {
      setDimensions(dimensionsData);
    } else if(!dimensionsData && !dimensionsError) {
      // If dimensions data is not present, fetch from the cache
      try {
        RefetchCache("DimensionsData", queryClient);
      } catch (error) {
        console.error("Error refetching DimensionsData:", error);
      }
    }

    // If question options data is present, set the state
    if (questionOptionsData) {
      setQuestionOption(questionOptionsData);
    } else   if (!questionOptionsData && !questionOptionsError)  {
      // If question options data is not present, fetch from the cache
      try {
        RefetchCache("questionOptionsData",queryClient);
      } catch (error) {
        console.error("Error refetching questionOptionsData:", error);
      }
    }

    if(dimensionsData && questionOptionsData)
    setLoading(false); 
  }, [dimensionsData, questionOptionsData]);

   // Create a function to merge answers based on questionID and dimensionID
   const mergeAnswers = async (
    existingAnswers: Dimension[],
    newAnswers: Dimension[]
  ) => {
    const mergedAnswers = [...existingAnswers];

   newAnswers.forEach(async (newAnswer) => {
      const existingDimensionIndex = existingAnswers.findIndex(
        (existingDimension) => existingDimension.ID === newAnswer.ID
      );

      if (existingDimensionIndex !== -1) {
        // If a dimension with the same ID exists, update it
        const existingDimension = existingAnswers[existingDimensionIndex];
      newAnswer.questions.forEach((newQuestion) => {
          const existingQuestionIndex = existingDimension.questions.findIndex(
            (existingQuestion) => existingQuestion.ID === newQuestion.ID
          );

          if (existingQuestionIndex !== -1) {
            // If a question with the same ID exists, update it
            existingDimension.questions[existingQuestionIndex] = newQuestion;
          } else {
            // Otherwise, add the new question
            existingDimension.questions.push(newQuestion);
          }
        });
        existingDimension.Percentage = newAnswer.Percentage;
        existingDimension.LevelId = newAnswer.LevelId;
        existingDimension.Level = newAnswer.Level;
        existingDimension.Narrative = newAnswer.Narrative;
        mergedAnswers[existingDimensionIndex] = existingDimension;
      } else {
        // Otherwise, add the new dimension
        mergedAnswers.push(newAnswer);
      }
    });

    return mergedAnswers;
  };

  const handleNext =async (newAnswers: Dimension[]) => {   
    
    const updatedDimensions =await mergeAnswers(selectedDimensions, newAnswers);
    setSelectedDimensions(updatedDimensions);
    if (activeStep < numberOfSteps - 1) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      //scroll to the top
      window.scrollTo(0, 0);
    } else if (activeStep === numberOfSteps - 1) {
     await generateExcel({ updatedDimensions });
      localStorage.setItem(
        "updatedDimensions",
        JSON.stringify(updatedDimensions)
      );
      navigate("/result", { state: { updatedDimensions } });
    }
  };

  const handleBack =async (newAnswers: Dimension[]) => {  
    if (activeStep > 0) {
      //Update latest Answers on back button click
      const updatedDimensions =await mergeAnswers(selectedDimensions, newAnswers);
      setSelectedDimensions(updatedDimensions);
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else if (activeStep === 0) {
      // Handle going back to a previous page or navigate to the desired URL.
      navigate("/");
    }
  };
  // const handleBack =async () => {  
  //   if (activeStep > 0) {
  //     //Update latest Answers on back button click
  //     // const updatedDimensions =await mergeAnswers(selectedDimensions, newAnswers);
  //     // setSelectedDimensions(updatedDimensions);
  //     setActiveStep((prevActiveStep) => prevActiveStep - 1);
  //     window.scrollTo({
  //       top: 0,
  //       behavior: "smooth",
  //     });
  //   } else if (activeStep === 0) {
  //     // Handle going back to a previous page or navigate to the desired URL.
  //     navigate("/");
  //   }
  // };

  return (
    <>
      {loading ? (
        <CircularProgressBar />
      ) : (
        <div>
          <AtomicAppBar />
          <Box sx={{ width: "100%" }}>
            <Stepper
              activeStep={activeStep}
              className="dimension-stepper-progress-bar"
            >
              {dimensions.map((dimension) => (
                <Step key={dimension.ID}>
                  <StepLabel>
                    <AtomicTypography className="dimension-stepper-progress-bar-label">
                      {" "}
                      {dimension.Name}
                    </AtomicTypography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Heading
              className="Dimension-header"
              title={dimensions[activeStep]?.Name || ""}
            />
            <QuestionAnswer
              dimensionID={activeStep + 1}
              handleNext={handleNext} // Pass handleNext as a prop
              handleBack={handleBack} // Pass handleBack as a prop
              selectedDimension={selectedDimensions}
              setSelectedDimension={setSelectedDimensions}
              questionOptionsData={questionOption}
              dimensionsData={dimensions}
            />
          </Box>
        </div>
      )}
    </>
  );
};

export default DimensionStepper;
